<script lang="ts">
  import { onMount } from 'svelte';
  import { 
    Siren, TriangleAlert, ShieldAlert, Clock, Wrench, CheckCircle2, 
    BellOff, ShieldCheck, Droplets, Power, Send, Search, Filter, 
    RefreshCw, ExternalLink, AlertCircle, ThermometerSnowflake, 
    FileText, Check, ChevronDown, Building2, Flame
  } from 'lucide-svelte';
  import { deviceHref } from '../utils/navigation';
  import { formatFlowAlarm } from '../utils/alarms';
  import { user } from '../stores/user';

  interface IncidentItem {
    incident_id: string;
    device_id: number;
    device_eui: string;
    device_name: string;
    property_id: number;
    property_name: string;
    unit_number?: string;
    incident_type: string;
    severity: 'critical' | 'urgent' | 'warning' | 'info';
    title: string;
    description: string;
    current_flow_gpm: number;
    est_loss_gph: number;
    est_daily_cost_usd: number;
    detected_at: string;
    status: 'active' | 'acknowledged' | 'snoozed' | 'work_order_dispatched';
    is_snoozed: boolean;
    snoozed_until?: string;
    snooze_reason?: string;
    acknowledged_by?: number;
    acknowledged_at?: string;
    work_order_id?: string;
    work_order_number?: string;
    valve_position?: number;
  }

  interface IncidentMetrics {
    total_active_incidents: number;
    critical_burst_count: number;
    continuous_leak_count: number;
    freeze_warning_count: number;
    snoozed_count: number;
    work_order_dispatched_count: number;
    mean_time_to_detect_minutes: number;
    mean_time_to_resolve_minutes: number;
    total_estimated_loss_gph: number;
    total_estimated_cost_daily: number;
  }

  // Props
  let { 
    initialPropertyId = undefined,
    onIncidentCountChange = undefined
  }: {
    initialPropertyId?: number;
    onIncidentCountChange?: (count: number) => void;
  } = $props();

  // State
  let loading = $state(true);
  let errorMsg = $state('');
  let incidents = $state<IncidentItem[]>([]);
  let metrics = $state<IncidentMetrics>({
    total_active_incidents: 0,
    critical_burst_count: 0,
    continuous_leak_count: 0,
    freeze_warning_count: 0,
    snoozed_count: 0,
    work_order_dispatched_count: 0,
    mean_time_to_detect_minutes: 3.8,
    mean_time_to_resolve_minutes: 18.5,
    total_estimated_loss_gph: 0,
    total_estimated_cost_daily: 0
  });

  // Filters
  let searchQuery = $state('');
  let selectedPropertyId = $state<string>('all');
  $effect(() => {
    if (initialPropertyId) {
      selectedPropertyId = String(initialPropertyId);
    }
  });
  let selectedSeverity = $state<string>('all');
  let selectedStatus = $state<string>('all');

  // Selection
  let selectedIncidentIds = $state<Set<string>>(new Set());

  // Action Modals State
  let showSnoozeModal = $state(false);
  let snoozeDurationMinutes = $state(60);
  let snoozeReason = $state('Plumbing maintenance scheduled');
  let isSubmittingSnooze = $state(false);

  let showWorkOrderModal = $state(false);
  let woPriority = $state('urgent');
  let woCategory = $state('plumbing_fixture');
  let woPMSTarget = $state('appfolio');
  let woNotes = $state('Investigate fixture telemetry alert and continuous baseline flow.');
  let isSubmittingWO = $state(false);
  let lastCreatedWorkOrders = $state<any[]>([]);
  let showWOSuccessModal = $state(false);

  let showShutoffConfirmModal = $state(false);
  let shutoffReason = $state('Emergency incident mitigation');
  let isSubmittingShutoff = $state(false);

  let toastMessage = $state('');
  let toastType = $state<'success' | 'error' | 'info'>('success');

  function showToast(msg: string, type: 'success' | 'error' | 'info' = 'success') {
    toastMessage = msg;
    toastType = type;
    setTimeout(() => {
      toastMessage = '';
    }, 4500);
  }

  let currentUser = $derived($user);
  let isGlobalAdmin = $derived(currentUser?.user_type === 'system_admin');
  let userPropertyIds = $derived.by(() => {
    if (!currentUser || isGlobalAdmin) return null;
    return new Set((currentUser.properties || []).map((p: any) => Number(p.property_id)));
  });

  // Fetch Matrix
  export async function loadIncidents() {
    loading = true;
    errorMsg = '';
    try {
      const res = await fetch('/api/alerts/incidents');
      if (!res.ok) {
        throw new Error(`Failed to load incident matrix: ${res.statusText}`);
      }
      const data = await res.json();
      metrics = data.metrics || metrics;
      incidents = data.incidents || [];
      if (onIncidentCountChange) {
        onIncidentCountChange(metrics.total_active_incidents);
      }
    } catch (e: any) {
      errorMsg = e.message || 'Error connecting to incident command server';
      incidents = [];
      metrics = {
        total_active_incidents: 0,
        critical_burst_count: 0,
        continuous_leak_count: 0,
        freeze_warning_count: 0,
        snoozed_count: 0,
        work_order_dispatched_count: 0,
        mean_time_to_detect_minutes: 0,
        mean_time_to_resolve_minutes: 0,
        total_estimated_loss_gph: 0,
        total_estimated_cost_daily: 0
      };
      if (onIncidentCountChange) {
        onIncidentCountChange(0);
      }
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    loadIncidents();
  });

  // Unique properties for dropdown
  let propertyOptions = $derived.by(() => {
    const map = new Map<number, string>();
    if (userPropertyIds) {
      for (const p of (currentUser?.properties || [])) {
        if (p.property_id) {
          map.set(Number(p.property_id), p.property_name || `Property #${p.property_id}`);
        }
      }
    } else {
      for (const inc of incidents) {
        map.set(inc.property_id, inc.property_name);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  });

  // Filtered incidents
  let filteredIncidents = $derived.by(() => {
    return incidents.filter(inc => {
      // Scope filter: non-global users must only see incidents on their assigned properties
      if (userPropertyIds && !userPropertyIds.has(inc.property_id)) {
        return false;
      }
      // Property filter
      if (selectedPropertyId !== 'all' && String(inc.property_id) !== selectedPropertyId) {
        return false;
      }
      // Severity filter
      if (selectedSeverity !== 'all' && inc.severity !== selectedSeverity) {
        return false;
      }
      // Status filter
      if (selectedStatus !== 'all') {
        if (selectedStatus === 'active' && inc.status !== 'active') return false;
        if (selectedStatus === 'snoozed' && !inc.is_snoozed) return false;
        if (selectedStatus === 'acknowledged' && inc.status !== 'acknowledged') return false;
        if (selectedStatus === 'work_order_dispatched' && inc.status !== 'work_order_dispatched') return false;
      }
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = inc.device_name.toLowerCase().includes(q);
        const matchEui = inc.device_eui.toLowerCase().includes(q);
        const matchProp = inc.property_name.toLowerCase().includes(q);
        const matchUnit = (inc.unit_number || '').toLowerCase().includes(q);
        const matchDesc = inc.description.toLowerCase().includes(q);
        if (!matchName && !matchEui && !matchProp && !matchUnit && !matchDesc) {
          return false;
        }
      }
      return true;
    });
  });

  // Selected incident objects
  let selectedIncidents = $derived.by(() => {
    return incidents.filter(inc => selectedIncidentIds.has(inc.incident_id));
  });

  let selectedDeviceIds = $derived(selectedIncidents.map(i => i.device_id));

  let isAllSelected = $derived(
    filteredIncidents.length > 0 && 
    filteredIncidents.every(inc => selectedIncidentIds.has(inc.incident_id))
  );

  function toggleSelectAll() {
    if (isAllSelected) {
      selectedIncidentIds.clear();
      selectedIncidentIds = new Set(selectedIncidentIds);
    } else {
      const next = new Set<string>();
      filteredIncidents.forEach(inc => next.add(inc.incident_id));
      selectedIncidentIds = next;
    }
  }

  function toggleSelectRow(id: string) {
    if (selectedIncidentIds.has(id)) {
      selectedIncidentIds.delete(id);
    } else {
      selectedIncidentIds.add(id);
    }
    selectedIncidentIds = new Set(selectedIncidentIds);
  }

  // Bulk Actions
  async function handleBulkAcknowledge() {
    if (selectedDeviceIds.length === 0) return;
    try {
      const res = await fetch('/api/alerts/bulk-acknowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_ids: selectedDeviceIds,
          alert_kind: 'all'
        })
      });
      if (!res.ok) throw new Error('Acknowledge failed');
      
      // Update local state
      incidents = incidents.map(inc => {
        if (selectedDeviceIds.includes(inc.device_id)) {
          return {
            ...inc,
            status: 'acknowledged',
            acknowledged_at: new Date().toISOString()
          };
        }
        return inc;
      });
      showToast(`Acknowledged ${selectedDeviceIds.length} incidents successfully`, 'success');
      selectedIncidentIds.clear();
      selectedIncidentIds = new Set(selectedIncidentIds);
    } catch (e: any) {
      showToast(e.message || 'Error acknowledging alerts', 'error');
    }
  }

  async function handleBulkSnoozeSubmit() {
    if (selectedDeviceIds.length === 0) return;
    isSubmittingSnooze = true;
    try {
      const res = await fetch('/api/alerts/bulk-snooze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_ids: selectedDeviceIds,
          duration_minutes: snoozeDurationMinutes,
          reason: snoozeReason,
          alert_kind: 'all'
        })
      });
      if (!res.ok) throw new Error('Snooze request failed');
      const data = await res.json();
      const untilStr = data.snoozed_until || new Date(Date.now() + snoozeDurationMinutes * 60000).toISOString();

      incidents = incidents.map(inc => {
        if (selectedDeviceIds.includes(inc.device_id)) {
          return {
            ...inc,
            status: 'snoozed',
            is_snoozed: true,
            snoozed_until: untilStr,
            snooze_reason: snoozeReason
          };
        }
        return inc;
      });

      showToast(`Snoozed ${selectedDeviceIds.length} incident(s) for ${snoozeDurationMinutes >= 60 ? `${snoozeDurationMinutes / 60}h` : `${snoozeDurationMinutes}m`}`, 'info');
      showSnoozeModal = false;
      selectedIncidentIds.clear();
      selectedIncidentIds = new Set(selectedIncidentIds);
    } catch (e: any) {
      showToast(e.message || 'Error snoozing alerts', 'error');
    } finally {
      isSubmittingSnooze = false;
    }
  }

  async function handleBulkUnsnooze(deviceIds: number[]) {
    try {
      const res = await fetch('/api/alerts/bulk-unsnooze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_ids: deviceIds,
          alert_kind: 'all'
        })
      });
      if (!res.ok) throw new Error('Unsnooze failed');
      incidents = incidents.map(inc => {
        if (deviceIds.includes(inc.device_id)) {
          return {
            ...inc,
            status: 'active',
            is_snoozed: false,
            snoozed_until: undefined,
            snooze_reason: undefined
          };
        }
        return inc;
      });
      showToast(`Unsnoozed ${deviceIds.length} incident(s)`, 'success');
    } catch (e: any) {
      showToast(e.message || 'Error unsnoozing alerts', 'error');
    }
  }

  async function handleBulkWorkOrderSubmit() {
    if (selectedDeviceIds.length === 0) return;
    isSubmittingWO = true;
    try {
      const res = await fetch('/api/alerts/bulk-escalate-work-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_ids: selectedDeviceIds,
          priority: woPriority,
          category: woCategory,
          pms_target: woPMSTarget,
          notes: woNotes
        })
      });
      if (!res.ok) throw new Error('Work order dispatch failed');
      const data = await res.json();
      lastCreatedWorkOrders = data.work_orders || [];

      incidents = incidents.map(inc => {
        if (selectedDeviceIds.includes(inc.device_id)) {
          const matchWo = lastCreatedWorkOrders.find((w: any) => w.device_id === inc.device_eui || w.device_id === String(inc.device_id));
          return {
            ...inc,
            status: 'work_order_dispatched',
            work_order_id: matchWo ? matchWo.work_order_id : undefined,
            work_order_number: matchWo ? matchWo.order_number : `WO-${woPMSTarget.toUpperCase()}-${inc.property_id}`
          };
        }
        return inc;
      });

      showWorkOrderModal = false;
      showWOSuccessModal = true;
      selectedIncidentIds.clear();
      selectedIncidentIds = new Set(selectedIncidentIds);
    } catch (e: any) {
      showToast(e.message || 'Error dispatching work orders', 'error');
    } finally {
      isSubmittingWO = false;
    }
  }

  async function handleBulkEmergencyShutoff() {
    if (selectedDeviceIds.length === 0) return;
    isSubmittingShutoff = true;
    try {
      const res = await fetch('/api/alerts/bulk-emergency-shutoff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_ids: selectedDeviceIds,
          reason: shutoffReason
        })
      });
      if (!res.ok) throw new Error('Emergency shutoff failed');
      const data = await res.json();

      incidents = incidents.map(inc => {
        if (selectedDeviceIds.includes(inc.device_id)) {
          return {
            ...inc,
            valve_position: 0
          };
        }
        return inc;
      });

      showToast(`Emergency shutoff dispatched to ${data.dispatched_count || selectedDeviceIds.length} valves!`, 'success');
      showShutoffConfirmModal = false;
      selectedIncidentIds.clear();
      selectedIncidentIds = new Set(selectedIncidentIds);
    } catch (e: any) {
      showToast(e.message || 'Error executing emergency shutoff', 'error');
    } finally {
      isSubmittingShutoff = false;
    }
  }

  function getSeverityBadge(sev: string) {
    switch (sev) {
      case 'critical':
        return 'badge-error text-white font-black';
      case 'urgent':
        return 'badge-warning text-black font-black';
      case 'warning':
        return 'badge-warning badge-outline font-bold';
      default:
        return 'badge-ghost font-bold';
    }
  }

  function formatTimeAgo(iso: string) {
    if (!iso) return 'Just now';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }
</script>

<div class="space-y-6">

  <!-- Toast Notification -->
  {#if toastMessage}
    <div class="toast toast-top toast-end z-50">
      <div class="alert shadow-lg text-xs font-bold rounded-2xl {toastType === 'success' ? 'alert-success text-white' : toastType === 'error' ? 'alert-error text-white' : 'alert-info text-white'}">
        <span>{toastMessage}</span>
      </div>
    </div>
  {/if}

  <!-- Command Center Header & KPI Row -->
  <div class="bg-base-100 rounded-3xl p-6 sm:p-8 border border-base-200 shadow-sm space-y-6">
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div class="space-y-1.5">
        <div class="flex items-center gap-2">
          <span class="badge badge-error text-white font-black text-xs uppercase tracking-wider px-3 py-1 gap-1.5 animate-pulse">
            <Siren class="w-3.5 h-3.5" /> Incident Command Matrix
          </span>
          <span class="badge badge-ghost font-mono text-xs font-bold">
            {metrics.total_active_incidents} Active Alarms Requiring Action
          </span>
        </div>
        <h2 class="text-2xl font-black text-base-content tracking-tight">
          Portfolio-Wide Alarm Command & Incident Escalation Center
        </h2>
        <p class="text-xs sm:text-sm text-base-content/65 max-w-3xl leading-relaxed">
          Centralized triage command across all properties. Perform multi-device bulk acknowledgments, duration-based snoozing, automated PMS work order dispatch, and emergency shutoff isolation.
        </p>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <button 
          type="button" 
          class="btn btn-sm btn-ghost rounded-xl text-xs font-bold gap-1.5"
          onclick={loadIncidents}
          disabled={loading}
        >
          <RefreshCw class="w-3.5 h-3.5 {loading ? 'animate-spin' : ''}" />
          Refresh Triage
        </button>
      </div>
    </div>

    <!-- KPI Metric Cards Grid -->
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-2">
      <!-- MTTD -->
      <div class="p-3.5 rounded-2xl bg-base-200/50 border border-base-200">
        <div class="flex items-center justify-between text-base-content/60 text-[11px] font-bold">
          <span>MTTD (Detection)</span>
          <Clock class="w-3.5 h-3.5 text-primary" />
        </div>
        <div class="mt-1 text-xl font-black text-base-content tracking-tight">
          {metrics.mean_time_to_detect_minutes.toFixed(1)} <span class="text-xs font-bold text-base-content/50">min</span>
        </div>
        <div class="text-[10px] text-emerald-600 font-bold mt-0.5">Top 5% Portfolio Benchmark</div>
      </div>

      <!-- MTTR -->
      <div class="p-3.5 rounded-2xl bg-base-200/50 border border-base-200">
        <div class="flex items-center justify-between text-base-content/60 text-[11px] font-bold">
          <span>MTTR (Resolve)</span>
          <ShieldCheck class="w-3.5 h-3.5 text-emerald-500" />
        </div>
        <div class="mt-1 text-xl font-black text-base-content tracking-tight">
          {metrics.mean_time_to_resolve_minutes.toFixed(1)} <span class="text-xs font-bold text-base-content/50">min</span>
        </div>
        <div class="text-[10px] text-base-content/60 font-medium mt-0.5">Median time to dispatch</div>
      </div>

      <!-- Critical Bursts -->
      <div class="p-3.5 rounded-2xl bg-error/10 border border-error/20">
        <div class="flex items-center justify-between text-error text-[11px] font-bold">
          <span>Critical Bursts</span>
          <Flame class="w-3.5 h-3.5 text-error" />
        </div>
        <div class="mt-1 text-xl font-black text-error tracking-tight">
          {metrics.critical_burst_count}
        </div>
        <div class="text-[10px] text-error/80 font-bold mt-0.5">High-flow pipe breach</div>
      </div>

      <!-- Continuous Leaks -->
      <div class="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20">
        <div class="flex items-center justify-between text-amber-700 dark:text-amber-300 text-[11px] font-bold">
          <span>Continuous Leaks</span>
          <Droplets class="w-3.5 h-3.5 text-amber-600" />
        </div>
        <div class="mt-1 text-xl font-black text-amber-700 dark:text-amber-300 tracking-tight">
          {metrics.continuous_leak_count}
        </div>
        <div class="text-[10px] text-amber-700/80 dark:text-amber-300/80 font-medium mt-0.5">Toilet flapper / pinhole</div>
      </div>

      <!-- Freeze Warnings -->
      <div class="p-3.5 rounded-2xl bg-cyan-500/10 border border-cyan-500/20">
        <div class="flex items-center justify-between text-cyan-700 dark:text-cyan-300 text-[11px] font-bold">
          <span>Freeze Risks</span>
          <ThermometerSnowflake class="w-3.5 h-3.5 text-cyan-600" />
        </div>
        <div class="mt-1 text-xl font-black text-cyan-700 dark:text-cyan-300 tracking-tight">
          {metrics.freeze_warning_count}
        </div>
        <div class="text-[10px] text-cyan-700/80 dark:text-cyan-300/80 font-medium mt-0.5">&lt; 36°F frost hazard</div>
      </div>

      <!-- Est. Financial Impact -->
      <div class="p-3.5 rounded-2xl bg-base-200/50 border border-base-200">
        <div class="flex items-center justify-between text-base-content/60 text-[11px] font-bold">
          <span>Daily Loss Rate</span>
          <Droplets class="w-3.5 h-3.5 text-primary" />
        </div>
        <div class="mt-1 text-xl font-black text-base-content tracking-tight">
          ${metrics.total_estimated_cost_daily.toFixed(0)} <span class="text-xs font-bold text-base-content/50">/ day</span>
        </div>
        <div class="text-[10px] text-base-content/60 font-mono mt-0.5">{metrics.total_estimated_loss_gph.toFixed(1)} GPH leaking</div>
      </div>
    </div>
  </div>

  <!-- Filter & Action Toolbar -->
  <div class="bg-base-100 rounded-3xl p-5 border border-base-200 shadow-sm space-y-4">
    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
      
      <!-- Search & Dropdown Filters -->
      <div class="flex flex-wrap items-center gap-2.5 flex-1">
        <!-- Search Input -->
        <div class="relative min-w-[220px] flex-1 sm:flex-initial">
          <Search class="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
          <input 
            type="text" 
            bind:value={searchQuery}
            placeholder="Search device, EUI, unit, or title..." 
            class="input input-sm input-bordered pl-8.5 rounded-xl text-xs w-full"
          />
        </div>

        <!-- Property Dropdown -->
        <select 
          bind:value={selectedPropertyId} 
          class="select select-bordered select-sm rounded-xl text-xs font-semibold"
        >
          <option value="all">🏢 All Properties ({propertyOptions.length})</option>
          {#each propertyOptions as p}
            <option value={String(p.id)}>{p.name} (#{p.id})</option>
          {/each}
        </select>

        <!-- Severity Filter Buttons -->
        <div class="join border border-base-300 rounded-xl overflow-hidden">
          <button 
            type="button" 
            class="join-item btn btn-xs font-bold {selectedSeverity === 'all' ? 'btn-neutral' : 'btn-ghost'}"
            onclick={() => selectedSeverity = 'all'}
          >
            All
          </button>
          <button 
            type="button" 
            class="join-item btn btn-xs font-bold {selectedSeverity === 'critical' ? 'btn-error text-white' : 'btn-ghost text-error'}"
            onclick={() => selectedSeverity = 'critical'}
          >
            Critical
          </button>
          <button 
            type="button" 
            class="join-item btn btn-xs font-bold {selectedSeverity === 'urgent' ? 'btn-warning text-black' : 'btn-ghost text-warning'}"
            onclick={() => selectedSeverity = 'urgent'}
          >
            Urgent
          </button>
          <button 
            type="button" 
            class="join-item btn btn-xs font-bold {selectedSeverity === 'warning' ? 'btn-neutral' : 'btn-ghost'}"
            onclick={() => selectedSeverity = 'warning'}
          >
            Advisory
          </button>
        </div>

        <!-- Status Filter Buttons -->
        <div class="join border border-base-300 rounded-xl overflow-hidden">
          <button 
            type="button" 
            class="join-item btn btn-xs font-bold {selectedStatus === 'all' ? 'btn-neutral' : 'btn-ghost'}"
            onclick={() => selectedStatus = 'all'}
          >
            All Status
          </button>
          <button 
            type="button" 
            class="join-item btn btn-xs font-bold {selectedStatus === 'active' ? 'btn-primary' : 'btn-ghost'}"
            onclick={() => selectedStatus = 'active'}
          >
            Active
          </button>
          <button 
            type="button" 
            class="join-item btn btn-xs font-bold {selectedStatus === 'snoozed' ? 'btn-primary' : 'btn-ghost'}"
            onclick={() => selectedStatus = 'snoozed'}
          >
            Snoozed
          </button>
          <button 
            type="button" 
            class="join-item btn btn-xs font-bold {selectedStatus === 'acknowledged' ? 'btn-primary' : 'btn-ghost'}"
            onclick={() => selectedStatus = 'acknowledged'}
          >
            Ack'd
          </button>
        </div>
      </div>

      <!-- Bulk Actions Bar (Active when >= 1 item selected) -->
      {#if selectedDeviceIds.length > 0}
        <div class="flex items-center gap-2 p-1.5 bg-base-200/80 rounded-2xl border border-primary/20 shrink-0 animate-in fade-in slide-in-from-top-2">
          <span class="text-xs font-black text-primary px-2">
            {selectedDeviceIds.length} Selected
          </span>

          <button 
            type="button" 
            class="btn btn-xs btn-outline btn-success rounded-xl font-bold gap-1"
            onclick={handleBulkAcknowledge}
            data-testid="bulk-ack-btn"
            title="Mark selected incidents as acknowledged"
          >
            <Check class="w-3.5 h-3.5" /> Acknowledge
          </button>

          <button 
            type="button" 
            class="btn btn-xs btn-outline btn-warning rounded-xl font-bold gap-1"
            onclick={() => showSnoozeModal = true}
            data-testid="bulk-snooze-btn"
            title="Silence alarms for a temporary maintenance window"
          >
            <BellOff class="w-3.5 h-3.5" /> Snooze
          </button>

          <button 
            type="button" 
            class="btn btn-xs btn-primary rounded-xl font-bold gap-1 shadow-xs"
            onclick={() => showWorkOrderModal = true}
            data-testid="bulk-work-order-btn"
            title="Dispatch work orders to property management software"
          >
            <Wrench class="w-3.5 h-3.5" /> Work Order
          </button>

          <button 
            type="button" 
            class="btn btn-xs btn-error text-white rounded-xl font-bold gap-1 shadow-xs"
            onclick={() => showShutoffConfirmModal = true}
            data-testid="bulk-shutoff-btn"
            title="Immediately actuate selected valves to 0% closed"
          >
            <Power class="w-3.5 h-3.5" /> Emergency Shutoff
          </button>
        </div>
      {/if}

    </div>
  </div>

  <!-- Incident Matrix Table -->
  <div class="bg-base-100 rounded-3xl border border-base-200 overflow-hidden shadow-sm">
    {#if loading && incidents.length === 0}
      <div class="p-16 text-center text-xs text-base-content/50 space-y-2">
        <RefreshCw class="w-6 h-6 animate-spin mx-auto text-primary" />
        <p>Scanning portfolio telemetry and aggregating incident matrix...</p>
      </div>
    {:else if filteredIncidents.length === 0}
      <div class="p-16 text-center text-xs text-base-content/50 space-y-2">
        <CheckCircle2 class="w-10 h-10 text-emerald-500 mx-auto" />
        <p class="text-sm font-black text-base-content">All Clear — Zero Incidents Match Filter</p>
        <p class="max-w-md mx-auto text-base-content/60">
          No alarms or threshold violations match the current criteria across your monitored portfolio.
        </p>
      </div>
    {:else}
      <div class="overflow-x-auto">
        <table class="table w-full text-xs">
          <thead>
            <tr class="bg-base-200/50 text-base-content/60 uppercase font-black tracking-wider text-[10px]">
              <th class="w-10 text-center">
                <input 
                  type="checkbox" 
                  checked={isAllSelected}
                  onchange={toggleSelectAll}
                  class="checkbox checkbox-xs checkbox-primary rounded"
                />
              </th>
              <th>Severity & Status</th>
              <th>Target Valve / Sensor</th>
              <th>Property & Unit</th>
              <th>Diagnostic Findings & Impact</th>
              <th>Time Detected</th>
              <th class="text-right pr-4">Direct Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-base-200/50">
            {#each filteredIncidents as inc (inc.incident_id)}
              <tr class="hover:bg-base-200/30 transition-colors {selectedIncidentIds.has(inc.incident_id) ? 'bg-primary/5' : ''}">
                <!-- Checkbox -->
                <td class="text-center">
                  <input 
                    type="checkbox" 
                    checked={selectedIncidentIds.has(inc.incident_id)}
                    onchange={() => toggleSelectRow(inc.incident_id)}
                    class="checkbox checkbox-xs checkbox-primary rounded"
                  />
                </td>

                <!-- Severity & Status -->
                <td>
                  <div class="space-y-1">
                    <span class="badge badge-xs {getSeverityBadge(inc.severity)} uppercase tracking-wider">
                      {#if inc.severity === 'critical'}
                        <Flame class="w-3 h-3 mr-0.5 inline" />
                      {/if}
                      {inc.severity}
                    </span>

                    <div>
                      {#if inc.is_snoozed}
                        <span class="badge badge-xs badge-outline badge-warning font-mono font-bold">
                          Snoozed
                        </span>
                      {:else if inc.status === 'acknowledged'}
                        <span class="badge badge-xs badge-outline badge-success font-mono font-bold">
                          Ack'd
                        </span>
                      {:else if inc.status === 'work_order_dispatched'}
                        <span class="badge badge-xs badge-outline badge-info font-mono font-bold">
                          Work Order Dispatched
                        </span>
                      {:else}
                        <span class="badge badge-xs badge-error text-white font-mono font-bold animate-pulse">
                          Active Alarm
                        </span>
                      {/if}
                    </div>
                  </div>
                </td>

                <!-- Valve / Device Info -->
                <td>
                  <div class="font-sans">
                    <a 
                      href={deviceHref(inc.device_eui)} 
                      class="font-black text-sm text-base-content hover:text-primary transition-colors flex items-center gap-1.5"
                    >
                      {inc.device_name}
                      <ExternalLink class="w-3 h-3 opacity-40 hover:opacity-100" />
                    </a>
                    <div class="text-[10px] text-base-content/50 font-mono mt-0.5 flex items-center gap-2">
                      <span>EUI: {inc.device_eui}</span>
                      {#if inc.valve_position !== undefined}
                        <span class="badge badge-xs font-mono {inc.valve_position === 0 ? 'badge-error text-white font-bold' : 'badge-ghost'}">
                          Valve {inc.valve_position}%
                        </span>
                      {/if}
                    </div>
                  </div>
                </td>

                <!-- Property & Unit -->
                <td>
                  <div class="font-sans">
                    <span class="font-bold text-base-content block">{inc.property_name}</span>
                    <span class="text-[11px] text-base-content/60 font-medium">
                      Unit: {inc.unit_number || 'Common Area / Main'}
                    </span>
                  </div>
                </td>

                <!-- Diagnostics & Impact -->
                <td>
                  <div class="space-y-1 max-w-sm">
                    <div class="font-black text-base-content text-xs flex items-center gap-1">
                      {inc.title}
                    </div>
                    <p class="text-[11px] text-base-content/70 leading-tight">
                      {inc.description}
                    </p>
                    {#if inc.est_loss_gph > 0}
                      <div class="flex items-center gap-2 text-[10px] font-mono">
                        <span class="text-error font-bold">{inc.current_flow_gpm.toFixed(2)} GPM</span>
                        <span class="text-base-content/30">•</span>
                        <span class="text-base-content/70">{inc.est_loss_gph.toFixed(0)} GPH</span>
                        <span class="text-base-content/30">•</span>
                        <span class="text-amber-600 font-bold">${inc.est_daily_cost_usd.toFixed(2)} / day loss</span>
                      </div>
                    {/if}
                    {#if inc.is_snoozed && inc.snooze_reason}
                      <div class="text-[10px] text-warning font-medium italic">
                        Reason: "{inc.snooze_reason}" (Expires {new Date(inc.snoozed_until || '').toLocaleTimeString()})
                      </div>
                    {/if}
                    {#if inc.work_order_number}
                      <div class="text-[10px] text-primary font-mono font-bold flex items-center gap-1">
                        <FileText class="w-3 h-3" /> Ticket: {inc.work_order_number}
                      </div>
                    {/if}
                  </div>
                </td>

                <!-- Time Detected -->
                <td class="whitespace-nowrap font-mono text-[11px] text-base-content/70">
                  <div class="font-bold">{formatTimeAgo(inc.detected_at)}</div>
                  <div class="text-[9px] text-base-content/40">{new Date(inc.detected_at).toLocaleTimeString()}</div>
                </td>

                <!-- Direct Actions -->
                <td class="text-right pr-4">
                  <div class="flex items-center justify-end gap-1">
                    {#if inc.is_snoozed}
                      <button 
                        type="button"
                        class="btn btn-xs btn-ghost text-warning hover:bg-warning/10 font-bold"
                        onclick={() => handleBulkUnsnooze([inc.device_id])}
                        title="Resume alarm monitoring"
                      >
                        Unsnooze
                      </button>
                    {:else}
                      <button 
                        type="button"
                        class="btn btn-xs btn-ghost text-base-content/60 hover:text-base-content hover:bg-base-200"
                        onclick={() => {
                          selectedIncidentIds.clear();
                          selectedIncidentIds.add(inc.incident_id);
                          selectedIncidentIds = new Set(selectedIncidentIds);
                          showSnoozeModal = true;
                        }}
                        title="Snooze alarm temporarily"
                      >
                        <BellOff class="w-3.5 h-3.5" />
                      </button>
                    {/if}

                    <button 
                      type="button"
                      class="btn btn-xs btn-ghost text-primary hover:bg-primary/10"
                      onclick={() => {
                        selectedIncidentIds.clear();
                        selectedIncidentIds.add(inc.incident_id);
                        selectedIncidentIds = new Set(selectedIncidentIds);
                        showWorkOrderModal = true;
                      }}
                      title="Dispatch maintenance work order"
                    >
                      <Wrench class="w-3.5 h-3.5" />
                    </button>

                    <button 
                      type="button"
                      class="btn btn-xs btn-ghost text-error hover:bg-error/10"
                      onclick={() => {
                        selectedIncidentIds.clear();
                        selectedIncidentIds.add(inc.incident_id);
                        selectedIncidentIds = new Set(selectedIncidentIds);
                        showShutoffConfirmModal = true;
                      }}
                      title="Emergency shutoff isolation"
                    >
                      <Power class="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>

  <!-- SNOOZE MODAL -->
  {#if showSnoozeModal}
    <div class="modal modal-open z-50">
      <div class="modal-box max-w-md bg-base-100 rounded-3xl p-6 border border-base-200 shadow-2xl space-y-5">
        <div class="flex items-start justify-between border-b border-base-200 pb-3">
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-2xl bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold">
              <BellOff class="w-5 h-5" />
            </div>
            <div>
              <h3 class="font-black text-base text-base-content">Silence & Snooze Alarms</h3>
              <p class="text-xs text-base-content/60 font-medium">Temporary alarm suppression window for {selectedDeviceIds.length} device(s)</p>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-ghost btn-circle" onclick={() => showSnoozeModal = false}>✕</button>
        </div>

        <div class="space-y-4 text-xs">
          <!-- Duration presets -->
          <div class="form-control space-y-1.5">
            <span class="label py-0.5 text-xs font-bold text-base-content/70">Snooze Duration</span>
            <div class="grid grid-cols-4 gap-2">
              {#each [30, 60, 240, 1440] as min}
                <button 
                  type="button"
                  class="btn btn-xs rounded-xl font-bold {snoozeDurationMinutes === min ? 'btn-primary' : 'btn-ghost border border-base-300'}"
                  onclick={() => snoozeDurationMinutes = min}
                >
                  {min >= 1440 ? '24 Hours' : min >= 60 ? `${min / 60} Hours` : `${min} Min`}
                </button>
              {/each}
            </div>
          </div>

          <!-- Reason -->
          <div class="form-control">
            <label for="snooze-reason-input" class="label py-1 text-xs font-bold text-base-content/70">Operational Reason</label>
            <input 
              id="snooze-reason-input"
              type="text" 
              bind:value={snoozeReason}
              placeholder="e.g. Plumbing contractor replacing toilet flapper"
              class="input input-sm input-bordered rounded-xl text-xs"
            />
          </div>

          <div class="p-3 bg-amber-500/10 rounded-2xl border border-amber-500/20 text-[11px] text-base-content/80">
            <strong>Notice:</strong> Snoozing prevents repeated tenant and manager push/SMS dispatches. The valve will remain active and the incident will automatically un-snooze when the timer expires.
          </div>
        </div>

        <div class="flex items-center justify-end gap-2.5 pt-3 border-t border-base-200">
          <button type="button" class="btn btn-sm btn-ghost rounded-xl text-xs font-bold" onclick={() => showSnoozeModal = false}>
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-sm btn-warning text-black rounded-xl text-xs font-black uppercase tracking-wider px-5"
            onclick={handleBulkSnoozeSubmit}
            data-testid="confirm-snooze-btn"
            disabled={isSubmittingSnooze}
          >
            {#if isSubmittingSnooze}
              <span class="loading loading-spinner loading-xs"></span>
              Snoozing...
            {:else}
              Confirm Snooze
            {/if}
          </button>
        </div>
      </div>
      <button type="button" class="modal-backdrop bg-black/40 cursor-default" aria-label="Close modal" onclick={() => showSnoozeModal = false}></button>
    </div>
  {/if}

  <!-- WORK ORDER ESCALATION MODAL -->
  {#if showWorkOrderModal}
    <div class="modal modal-open z-50">
      <div class="modal-box max-w-lg bg-base-100 rounded-3xl p-6 sm:p-7 border border-base-200 shadow-2xl space-y-5">
        <div class="flex items-start justify-between border-b border-base-200 pb-3">
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-bold">
              <Wrench class="w-5 h-5" />
            </div>
            <div>
              <h3 class="font-black text-base text-base-content">Escalate to Maintenance Work Order</h3>
              <p class="text-xs text-base-content/60 font-medium">Dispatch maintenance tickets to PMS for {selectedDeviceIds.length} unit(s)</p>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-ghost btn-circle" onclick={() => showWorkOrderModal = false}>✕</button>
        </div>

        <div class="space-y-4 text-xs">
          <!-- Target PMS Platform -->
          <div class="form-control">
            <span class="label py-1 text-xs font-bold text-base-content/70">PMS Platform Integration</span>
            <select bind:value={woPMSTarget} class="select select-bordered select-sm rounded-xl text-xs font-bold">
              <option value="appfolio">AppFolio Property Manager</option>
              <option value="yardi">Yardi Voyager</option>
              <option value="buildium">Buildium</option>
              <option value="realpage">RealPage</option>
            </select>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <!-- Priority -->
            <div class="form-control">
              <span class="label py-1 text-xs font-bold text-base-content/70">Work Order Priority</span>
              <select bind:value={woPriority} class="select select-bordered select-sm rounded-xl text-xs font-semibold">
                <option value="emergency">🚨 Emergency (Immediate response)</option>
                <option value="urgent">⚠️ Urgent (Within 4 hours)</option>
                <option value="standard">Standard (Next business day)</option>
              </select>
            </div>

            <!-- Category -->
            <div class="form-control">
              <span class="label py-1 text-xs font-bold text-base-content/70">Maintenance Category</span>
              <select bind:value={woCategory} class="select select-bordered select-sm rounded-xl text-xs font-semibold">
                <option value="plumbing_fixture">Plumbing Fixture (Toilet, Sink)</option>
                <option value="supply_line">Supply Line / Riser</option>
                <option value="valve_repair">Valve / Actuator Replacement</option>
                <option value="freeze_mitigation">Freeze Mitigation / Pipe Insulation</option>
              </select>
            </div>
          </div>

          <!-- Notes -->
          <div class="form-control">
            <label for="wo-notes-input" class="label py-1 text-xs font-bold text-base-content/70">Technician Diagnostic Instructions</label>
            <textarea 
              id="wo-notes-input"
              bind:value={woNotes}
              rows="3"
              class="textarea textarea-bordered rounded-xl text-xs font-sans"
              placeholder="Instructions for maintenance technician..."
            ></textarea>
          </div>

          <!-- Summary Box -->
          <div class="p-3 bg-base-200/60 rounded-2xl border border-base-300 text-[11px] space-y-1">
            <div class="font-bold text-base-content">Automated Telemetry Payload:</div>
            <p class="text-base-content/70">
              Each generated work order includes exact telemetry loss rate (GPH), estimated daily cost, continuous flow duration, and live valve actuation commands.
            </p>
          </div>
        </div>

        <div class="flex items-center justify-end gap-2.5 pt-3 border-t border-base-200">
          <button type="button" class="btn btn-sm btn-ghost rounded-xl text-xs font-bold" onclick={() => showWorkOrderModal = false}>
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-sm btn-primary rounded-xl text-xs font-black uppercase tracking-wider px-5 gap-1.5"
            onclick={handleBulkWorkOrderSubmit}
            data-testid="dispatch-work-order-btn"
            disabled={isSubmittingWO}
          >
            {#if isSubmittingWO}
              <span class="loading loading-spinner loading-xs"></span>
              Dispatching...
            {:else}
              <Send class="w-3.5 h-3.5" /> Dispatch Work Order ({selectedDeviceIds.length})
            {/if}
          </button>
        </div>
      </div>
      <button type="button" class="modal-backdrop bg-black/40 cursor-default" aria-label="Close modal" onclick={() => showWorkOrderModal = false}></button>
    </div>
  {/if}

  <!-- WORK ORDER SUCCESS MODAL -->
  {#if showWOSuccessModal}
    <div class="modal modal-open z-50">
      <div class="modal-box max-w-md bg-base-100 rounded-3xl p-6 border border-base-200 shadow-2xl space-y-4">
        <div class="text-center space-y-2">
          <div class="w-12 h-12 rounded-3xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mx-auto">
            <CheckCircle2 class="w-6 h-6" />
          </div>
          <h3 class="font-black text-lg text-base-content">Work Orders Dispatched Successfully!</h3>
          <p class="text-xs text-base-content/60">
            Tickets have been synced to your property management system ({woPMSTarget.toUpperCase()}) with full diagnostic metrics.
          </p>
        </div>

        <div class="max-h-48 overflow-y-auto divide-y divide-base-200/50 bg-base-200/40 rounded-2xl p-2 text-xs">
          {#each lastCreatedWorkOrders as wo}
            <div class="py-2 px-3 flex items-center justify-between">
              <div>
                <span class="font-mono font-bold text-primary">{wo.order_number}</span>
                <span class="text-[10px] text-base-content/60 block">{wo.location_label}</span>
              </div>
              <span class="badge badge-xs badge-success text-white font-bold uppercase">{wo.sync_status}</span>
            </div>
          {/each}
        </div>

        <button 
          type="button" 
          class="btn btn-sm btn-primary w-full rounded-xl text-xs font-bold"
          onclick={() => showWOSuccessModal = false}
        >
          Done
        </button>
      </div>
      <button type="button" class="modal-backdrop bg-black/40 cursor-default" aria-label="Close modal" onclick={() => showWOSuccessModal = false}></button>
    </div>
  {/if}

  <!-- EMERGENCY SHUTOFF CONFIRMATION MODAL -->
  {#if showShutoffConfirmModal}
    <div class="modal modal-open z-50">
      <div class="modal-box max-w-md bg-base-100 rounded-3xl p-6 border border-error/20 shadow-2xl space-y-4">
        <div class="flex items-center gap-3 text-error">
          <div class="w-10 h-10 rounded-2xl bg-error/10 flex items-center justify-center shrink-0">
            <Power class="w-5 h-5 text-error" />
          </div>
          <div>
            <h3 class="font-black text-base text-base-content">Confirm Emergency Shutoff</h3>
            <p class="text-xs text-error font-bold">Immediate isolation of {selectedDeviceIds.length} water supply valve(s)</p>
          </div>
        </div>

        <p class="text-xs text-base-content/75 leading-relaxed">
          Are you sure you want to close valves for all {selectedDeviceIds.length} selected device(s)? This will stop water delivery to the selected units immediately to mitigate catastrophic flood damage.
        </p>

        <div class="form-control">
          <label for="shutoff-reason-input" class="label py-1 text-xs font-bold text-base-content/70">Audit Reason</label>
          <input 
            id="shutoff-reason-input"
            type="text" 
            bind:value={shutoffReason} 
            class="input input-sm input-bordered rounded-xl text-xs"
          />
        </div>

        <div class="flex items-center justify-end gap-2.5 pt-3 border-t border-base-200">
          <button type="button" class="btn btn-sm btn-ghost rounded-xl text-xs font-bold" onclick={() => showShutoffConfirmModal = false}>
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-sm btn-error text-white rounded-xl text-xs font-black uppercase tracking-wider px-5 gap-1.5"
            onclick={handleBulkEmergencyShutoff}
            data-testid="confirm-shutoff-btn"
            disabled={isSubmittingShutoff}
          >
            {#if isSubmittingShutoff}
              <span class="loading loading-spinner loading-xs"></span>
              Isolating...
            {:else}
              <Power class="w-3.5 h-3.5" /> Confirm Emergency Shutoff
            {/if}
          </button>
        </div>
      </div>
      <button type="button" class="modal-backdrop bg-black/40 cursor-default" aria-label="Close modal" onclick={() => showShutoffConfirmModal = false}></button>
    </div>
  {/if}

</div>
